import { HttpProvider } from './http.provider';
import { RequestInterceptor } from './http.provider.request.interceptor';
import { ResponseInterceptor } from './http.provider.response.interceptor';
import { SpinnerService } from '../SpinnerService';
import { IAppConfig } from './types/IApplicationConfig';

/**
 * Factory for creating singleton HttpProvider instances
 * 
 * Ensures all services share the same HttpProvider and interceptors.
 * This prevents code duplication and ensures consistent HTTP behavior
 * across all services.
 * 
 * Usage:
 *   HttpProviderFactory.create(config, spinnerService)
 *   ? Returns singleton HttpProvider
 *   
 *   HttpProviderFactory.getInstance()
 *   ? Gets the singleton instance
 */
export class HttpProviderFactory {
    private static httpProvider: HttpProvider | null = null;
    private static requestInterceptor: RequestInterceptor | null = null;
    private static responseInterceptor: ResponseInterceptor | null = null;
    private static config: IAppConfig | null = null;

    /**
     * Create or get HttpProvider instance (singleton pattern)
     * 
     * First call creates the HttpProvider and interceptors.
     * Subsequent calls return the cached instance.
     * 
     * @param config - Application configuration with API endpoint
     * @param spinnerService - Spinner service for loading indicators
     * @returns Singleton HttpProvider instance
     * @throws Error if config is invalid
     */
    static create(
        config: IAppConfig,
        spinnerService: SpinnerService
    ): HttpProvider {
        // Return existing instance if already created
        if (HttpProviderFactory.httpProvider) {
            return HttpProviderFactory.httpProvider;
        }

        // Validate configuration
        if (!config || !config.apiEndpoint) {
            throw new Error('HttpProviderFactory: Invalid configuration provided');
        }

        // Store configuration
        HttpProviderFactory.config = config;

        // ? Create ResponseInterceptor first (needs config)
        console.log('?? Creating ResponseInterceptor (singleton)');
        HttpProviderFactory.responseInterceptor = new ResponseInterceptor(config);

        // ? Create RequestInterceptor (needs config and responseInterceptor)
        console.log('?? Creating RequestInterceptor (singleton)');
        HttpProviderFactory.requestInterceptor = new RequestInterceptor(config, HttpProviderFactory.responseInterceptor);

        console.log('?? Creating HttpProvider (singleton)');

        // ? Create HttpProvider (shared by all services)
        HttpProviderFactory.httpProvider = new HttpProvider(
            HttpProviderFactory.requestInterceptor,
            HttpProviderFactory.responseInterceptor,
            spinnerService,
            config
        );

        console.log('? HttpProvider created and cached');

        return HttpProviderFactory.httpProvider;
    }

    /**
     * Create HttpProvider with automatic SpinnerService creation if needed
     * 
     * Convenience method that creates SpinnerService if not provided,
     * then creates/returns the singleton HttpProvider.
     * 
     * ? RECOMMENDED: Use this method in all service factories
     * ? Reduces boilerplate code
     * ? Ensures consistent initialization
     * 
     * @param config - Application configuration
     * @param spinnerService - Optional SpinnerService (creates new one if not provided)
     * @returns Singleton HttpProvider instance
     * 
     * Usage in service factories:
     *   const httpProvider = HttpProviderFactory.createHttpProvider(config, spinnerService);
     * 
     * Instead of:
     *   const spinner = spinnerService || new SpinnerService();
     *   const httpProvider = HttpProviderFactory.create(config, spinner);
     */
    static createHttpProvider(
        config: IAppConfig,
        spinnerService?: SpinnerService
    ): HttpProvider {
        const spinner = spinnerService || new SpinnerService();
        return HttpProviderFactory.create(config, spinner);
    }

    /**
     * Get RequestInterceptor instance
     * 
     * Used for accessing the interceptor directly if needed
     * (e.g., for subscribing to token updates)
     * 
     * @returns RequestInterceptor instance
     * @throws Error if HttpProvider not initialized
     */
    static getRequestInterceptor(): RequestInterceptor {
        if (!HttpProviderFactory.requestInterceptor) {
            throw new Error(
                'HttpProviderFactory: HttpProvider not initialized. ' +
                'Call create() first.'
            );
        }
        return HttpProviderFactory.requestInterceptor;
    }

    /**
     * Get ResponseInterceptor instance
     * 
     * Used for accessing the interceptor directly if needed
     * (e.g., for subscribing to header updates)
     * 
     * @returns ResponseInterceptor instance
     * @throws Error if HttpProvider not initialized
     */
    static getResponseInterceptor(): ResponseInterceptor {
        if (!HttpProviderFactory.responseInterceptor) {
            throw new Error(
                'HttpProviderFactory: HttpProvider not initialized. ' +
                'Call create() first.'
            );
        }
        return HttpProviderFactory.responseInterceptor;
    }

    /**
     * Get HttpProvider instance
     * 
     * Returns the singleton HttpProvider. This method is useful
     * if you need to access the HttpProvider after initial creation.
     * 
     * @returns HttpProvider instance
     * @throws Error if HttpProvider not initialized
     */
    static getInstance(): HttpProvider {
        if (!HttpProviderFactory.httpProvider) {
            throw new Error(
                'HttpProviderFactory: HttpProvider not initialized. ' +
                'Call create() first.'
            );
        }
        return HttpProviderFactory.httpProvider;
    }

    /**
     * Get stored configuration
     * 
     * @returns IAppConfig instance
     * @throws Error if HttpProvider not initialized
     */
    static getConfig(): IAppConfig {
        if (!HttpProviderFactory.config) {
            throw new Error(
                'HttpProviderFactory: HttpProvider not initialized. ' +
                'Call create() first.'
            );
        }
        return HttpProviderFactory.config;
    }

    /**
     * Check if HttpProvider is initialized
     * 
     * Useful for checking if the factory has already created the provider
     * 
     * @returns true if initialized, false otherwise
     */
    static isInitialized(): boolean {
        return HttpProviderFactory.httpProvider !== null;
    }

    /**
     * Reset all cached instances
     * 
     * Useful for testing and cleanup. After reset, the next call to create()
     * will create new instances.
     * 
     * WARNING: Only use this in testing or cleanup scenarios!
     */
    static reset(): void {
        if (HttpProviderFactory.httpProvider) {
            console.log('?? Resetting HttpProviderFactory...');
        }

        HttpProviderFactory.httpProvider = null;
        HttpProviderFactory.requestInterceptor = null;
        HttpProviderFactory.responseInterceptor = null;
        HttpProviderFactory.config = null;

        console.log('? HttpProviderFactory reset complete');
    }

    /**
     * Get factory statistics (for debugging)
     * 
     * @returns Object with current factory state
     */
    static getStats() {
        return {
            isInitialized: HttpProviderFactory.isInitialized(),
            hasHttpProvider: HttpProviderFactory.httpProvider !== null,
            hasRequestInterceptor: HttpProviderFactory.requestInterceptor !== null,
            hasResponseInterceptor: HttpProviderFactory.responseInterceptor !== null,
            hasConfig: HttpProviderFactory.config !== null,
            apiEndpoint: HttpProviderFactory.config?.apiEndpoint || 'Not set'
        };
    }
}
